import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Switch } from "./ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { X, Send, Bot, User, Loader2, Sparkles, Volume2, VolumeX, Calendar, BookOpen, FileText, ClipboardCheck, CalendarDays, Square } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${window.location.origin}/api`;

// Quick action buttons configuration
const QUICK_ACTIONS = [
  { id: "sessioni", label: "Sessioni", icon: CalendarDays, path: "/" },
  { id: "calendario", label: "Il Mio Calendario", icon: Calendar, path: "/calendario" },
  { id: "materiali", label: "I Miei Materiali", icon: FileText, path: "/i-miei-materiali" },
  { id: "biblioteca", label: "Biblioteca Corsi", icon: BookOpen, path: "/materiali" },
  { id: "test", label: "Test di Valutazione", icon: ClipboardCheck, path: "/quiz" },
];

// SOLEA Voice Configuration - Young Italian Female Voice
const SOLEA_VOICE_CONFIG = {
  lang: 'it-IT',
  rate: 0.92,        // Leggermente lento, calmo
  pitch: 1.15,       // Tono leggermente più alto = voce giovane femminile
  volume: 1.0,
  // Priority list of Italian female voices (best matches for young, warm voice)
  preferredVoices: [
    'Google italiano',           // Google Italian - natural
    'Microsoft Elsa Online',     // Microsoft Elsa - young female
    'Microsoft Isabella',        // Microsoft Isabella
    'Alice',                     // Apple Alice - Italian
    'Federica',                  // Apple Federica
    'Luca',                      // Fallback
    'it-IT-Standard-A',          // Google Cloud
    'it-IT-Wavenet-A',           // Google Cloud Wavenet
  ]
};

const SoleaChat = ({ isOpen, onClose, autoOpened = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `solea-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [currentSpeakingId, setCurrentSpeakingId] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const speechSynthRef = useRef(null);
  const utteranceQueueRef = useRef([]);

  // Initialize speech synthesis and get voices
  useEffect(() => {
    if ('speechSynthesis' in window) {
      speechSynthRef.current = window.speechSynthesis;
      
      const loadVoices = () => {
        const voices = speechSynthRef.current.getVoices();
        setAvailableVoices(voices);
        
        // Find best Italian female voice
        const italianVoices = voices.filter(v => v.lang.startsWith('it'));
        
        // Log available Italian voices for debugging
        console.log('🎤 Voci italiane disponibili:', italianVoices.map(v => ({
          name: v.name,
          lang: v.lang,
          local: v.localService
        })));
        
        // Find preferred voice
        let bestVoice = null;
        
        for (const preferredName of SOLEA_VOICE_CONFIG.preferredVoices) {
          bestVoice = italianVoices.find(v => 
            v.name.toLowerCase().includes(preferredName.toLowerCase())
          );
          if (bestVoice) break;
        }
        
        // If no preferred voice found, use any Italian female-sounding voice
        if (!bestVoice) {
          bestVoice = italianVoices.find(v => 
            v.name.toLowerCase().includes('female') ||
            v.name.toLowerCase().includes('donna') ||
            v.name.toLowerCase().includes('alice') ||
            v.name.toLowerCase().includes('elsa') ||
            v.name.toLowerCase().includes('federica') ||
            v.name.toLowerCase().includes('carla')
          ) || italianVoices[0];
        }
        
        if (bestVoice) {
          setSelectedVoice(bestVoice);
          console.log('🎤 Voce SOLEA selezionata:', bestVoice.name);
        }
      };
      
      // Load voices (they may load asynchronously)
      loadVoices();
      speechSynthRef.current.onvoiceschanged = loadVoices;
    }
    
    return () => {
      if (speechSynthRef.current) {
        speechSynthRef.current.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const userName = user?.name?.split(" ")[0] || "Utente";
      
      // Different welcome message if auto-opened after video
      const welcomeMessage = autoOpened 
        ? `Ciao **${userName}**! Sono SOLEA. Come posso aiutarti oggi?`
        : `Ciao **${userName}**, benvenuta/o! Sono **SOLEA**, la tua guida digitale ODR. 💜\n\nCome posso aiutarti?`;
      
      setMessages([{
        id: `msg-${Date.now()}`,
        role: "assistant",
        content: welcomeMessage,
        showQuickActions: true
      }]);
    }
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, user, autoOpened]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Prepare text for speech - add pauses and limit sentences
  const prepareTextForSpeech = useCallback((text) => {
    // Clean markdown
    let cleanText = text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/[#*_`]/g, '')
      .replace(/\n+/g, '. ')
      .replace(/\.+/g, '.')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Split into sentences
    const sentences = cleanText.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0);
    
    // Limit to first 4 sentences for natural listening
    const limitedSentences = sentences.slice(0, 4);
    
    // Add slight pauses between sentences (using commas)
    return limitedSentences.join(' ... ');
  }, []);

  // Text-to-Speech function with SOLEA voice
  const speakText = useCallback((text, messageId) => {
    if (!speechSynthRef.current) {
      toast.error("Text-to-Speech non supportato");
      return;
    }

    // Cancel any ongoing speech
    speechSynthRef.current.cancel();
    
    const preparedText = prepareTextForSpeech(text);
    
    const utterance = new SpeechSynthesisUtterance(preparedText);
    utterance.lang = SOLEA_VOICE_CONFIG.lang;
    utterance.rate = SOLEA_VOICE_CONFIG.rate;
    utterance.pitch = SOLEA_VOICE_CONFIG.pitch;
    utterance.volume = SOLEA_VOICE_CONFIG.volume;

    // Use selected SOLEA voice
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    utterance.onstart = () => {
      setSpeaking(true);
      setCurrentSpeakingId(messageId);
    };

    utterance.onend = () => {
      setSpeaking(false);
      setCurrentSpeakingId(null);
    };

    utterance.onerror = (e) => {
      console.error('Speech error:', e);
      setSpeaking(false);
      setCurrentSpeakingId(null);
    };

    speechSynthRef.current.speak(utterance);
  }, [selectedVoice, prepareTextForSpeech]);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    if (speechSynthRef.current) {
      speechSynthRef.current.cancel();
      setSpeaking(false);
      setCurrentSpeakingId(null);
    }
  }, []);

  const sendMessage = async (customMessage = null) => {
    const messageText = customMessage || input.trim();
    if (!messageText || loading) return;

    if (!customMessage) setInput("");
    
    const userMessageId = `msg-user-${Date.now()}`;
    setMessages(prev => [...prev, { id: userMessageId, role: "user", content: messageText }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/solea/chat`, {
        message: messageText,
        session_id: sessionId
      });
      
      const assistantMessageId = `msg-assistant-${Date.now()}`;
      const newMessage = { 
        id: assistantMessageId,
        role: "assistant", 
        content: response.data.response 
      };
      
      setMessages(prev => [...prev, newMessage]);

      // Auto-speak if voice enabled
      if (voiceEnabled) {
        setTimeout(() => speakText(response.data.response, assistantMessageId), 500);
      }
    } catch (error) {
      console.error("Solea chat error:", error);
      toast.error("Errore nella comunicazione con SOLEA");
      setMessages(prev => [...prev, { 
        id: `msg-error-${Date.now()}`,
        role: "assistant", 
        content: "Mi dispiace, si è verificato un errore. Riprova tra qualche istante." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickAction = (action) => {
    onClose();
    navigate(action.path);
  };

  const formatMessage = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] h-[650px] flex flex-col p-0 gap-0 rounded-2xl overflow-hidden">
        {/* Header */}
        <DialogHeader className="p-4 bg-gradient-to-r from-[#B87A8A] to-[#9B6A7A] text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-white font-semibold">SOLEA</DialogTitle>
                <p className="text-white/80 text-xs">Guida Digitale ODR</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Voice Toggle */}
              <div 
                className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 cursor-pointer hover:bg-white/20 transition-colors"
                onClick={() => {
                  const newValue = !voiceEnabled;
                  setVoiceEnabled(newValue);
                  if (!newValue) stopSpeaking();
                  if (newValue && selectedVoice) {
                    toast.success(`Voce SOLEA attivata`, { duration: 2000 });
                  }
                }}
              >
                {voiceEnabled ? (
                  <Volume2 className="w-4 h-4 text-white" />
                ) : (
                  <VolumeX className="w-4 h-4 text-white/60" />
                )}
                <span className="text-xs text-white/90 font-medium">
                  {voiceEnabled ? "ON" : "OFF"}
                </span>
                <Switch
                  checked={voiceEnabled}
                  onCheckedChange={(checked) => {
                    setVoiceEnabled(checked);
                    if (!checked) stopSpeaking();
                  }}
                  className="data-[state=checked]:bg-white/40 scale-75"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 rounded-full"
                onClick={onClose}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((msg) => (
              <div key={msg.id}>
                <div
                  className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.role === "assistant" && (
                    <div className="w-8 h-8 rounded-full bg-[#B87A8A]/10 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-[#B87A8A]" />
                    </div>
                  )}
                  <div className="flex flex-col gap-1 max-w-[80%]">
                    <div
                      className={`rounded-2xl px-4 py-3 ${
                        msg.role === "user"
                          ? "bg-[#B87A8A] text-white rounded-br-md"
                          : "bg-gray-100 text-gray-800 rounded-bl-md"
                      }`}
                    >
                      <div 
                        className="text-sm leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                      />
                    </div>
                    
                    {/* Listen button for assistant messages */}
                    {msg.role === "assistant" && (
                      <button
                        onClick={() => {
                          if (speaking && currentSpeakingId === msg.id) {
                            stopSpeaking();
                          } else {
                            speakText(msg.content, msg.id);
                          }
                        }}
                        className={`self-start flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-all ${
                          speaking && currentSpeakingId === msg.id
                            ? "bg-[#B87A8A] text-white shadow-md"
                            : "text-[#B87A8A] bg-[#B87A8A]/5 hover:bg-[#B87A8A]/15 border border-[#B87A8A]/20"
                        }`}
                      >
                        {speaking && currentSpeakingId === msg.id ? (
                          <>
                            <Square className="w-3 h-3" fill="currentColor" />
                            <span>Stop</span>
                          </>
                        ) : (
                          <>
                            <Volume2 className="w-3.5 h-3.5" />
                            <span>Ascolta</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {msg.role === "user" && (
                    <div className="w-8 h-8 rounded-full bg-[#C49A6C]/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-[#C49A6C]" />
                    </div>
                  )}
                </div>

                {/* Quick Actions after welcome message */}
                {msg.showQuickActions && (
                  <div className="mt-4 ml-11">
                    <p className="text-xs text-gray-500 mb-2">Accesso rapido:</p>
                    <div className="flex flex-wrap gap-2">
                      {QUICK_ACTIONS.map((action) => {
                        const Icon = action.icon;
                        return (
                          <button
                            key={action.id}
                            onClick={() => handleQuickAction(action)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#B87A8A]/30 rounded-full text-xs text-[#B87A8A] hover:bg-[#B87A8A]/10 hover:border-[#B87A8A]/50 transition-colors"
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {action.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 rounded-full bg-[#B87A8A]/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-[#B87A8A]" />
                </div>
                <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                  <Loader2 className="w-5 h-5 text-[#B87A8A] animate-spin" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Scrivi un messaggio..."
              className="flex-1 rounded-full border-gray-200 focus:border-[#B87A8A] focus:ring-[#B87A8A]"
              disabled={loading}
            />
            <Button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="rounded-full bg-[#B87A8A] hover:bg-[#A06878] w-10 h-10 p-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-400">
              SOLEA non fornisce consulenza medica
            </p>
            {selectedVoice && (
              <p className="text-xs text-gray-300">
                🎤 {selectedVoice.name.split(' ').slice(0, 2).join(' ')}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SoleaChat;
